import { useState, useEffect } from 'react';
import { getCurrentEnvironment } from '../utils/environment';

export type AppContext = 'telegram' | 'desktop-browser' | 'mobile-browser';

export const useAppContext = () => {
  const [appContext, setAppContext] = useState<AppContext>('telegram');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const environment = getCurrentEnvironment();
    
    // Detect if it's mobile browser
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      const isMobileViewport = window.innerWidth <= 768;
      
      return isMobileDevice || isMobileViewport;
    };

    // Update context and styles function
    const updateContext = (mobile: boolean) => {
      // Determine app context
      let context: AppContext;
      
      if (environment === 'telegram') {
        context = 'telegram';
      } else if (environment === 'browser') {
        context = mobile ? 'mobile-browser' : 'desktop-browser';
      } else {
        // PWA environment - treat as mobile browser
        context = 'mobile-browser';
      }

      // Update state only if context changed
      setAppContext(prevContext => {
        if (prevContext !== context) {
          // Update CSS custom properties and data attribute based on context
          const root = document.documentElement;
          root.setAttribute('data-app-context', context);
          
          // Log context change for debugging
          // console.log('App context changed from', prevContext, 'to', context);
          
          switch (context) {
            case 'telegram':
              root.style.setProperty('--app-context', 'telegram');
              root.style.setProperty('--header-offset', '0px');
              root.style.setProperty('--bottom-offset', '0px');
              root.style.setProperty('--content-padding', '16px');
              break;
            case 'desktop-browser':
              root.style.setProperty('--app-context', 'desktop-browser');
              root.style.setProperty('--header-offset', '60px');
              root.style.setProperty('--bottom-offset', '20px');
              root.style.setProperty('--content-padding', '24px');
              break;
            case 'mobile-browser':
              root.style.setProperty('--app-context', 'mobile-browser');
              root.style.setProperty('--header-offset', '60px');
              root.style.setProperty('--bottom-offset', '40px');
              root.style.setProperty('--content-padding', '16px');
              break;
          }
        }
        return context;
      });
    };

    const mobile = checkMobile();
    setIsMobile(mobile);
    updateContext(mobile);

    // Handle resize events
    const handleResize = () => {
      const newMobile = checkMobile();
      if (newMobile !== mobile) {
        setIsMobile(newMobile);
        if (environment === 'browser') {
          updateContext(newMobile);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return {
    appContext,
    isMobile,
    isTelegram: appContext === 'telegram',
    isDesktopBrowser: appContext === 'desktop-browser',
    isMobileBrowser: appContext === 'mobile-browser'
  };
}; 